/**
 * Validation schema and types for CreateBatchDialog.
 */

import * as yup from 'yup';
import type { ObjectSchema } from 'yup';
import { WorkflowFlowType, EntityAssignment, BatchPriority } from '../../../types/workflow';

/**
 * Form data structure for CreateBatchDialog.
 */
export interface CreateBatchFormData {
  // Common fields
  billingEntity: EntityAssignment | null;
  supplier: EntityAssignment | null;
  /** Optional Partner whose branding appears on co-branded reports/PDFs. */
  brand?: EntityAssignment | null;
  /** Optional Sales Agent (an Agent) whose Sage Sales Rep goes on the workflow's invoice. */
  salesAgent?: EntityAssignment | null;
  priority: BatchPriority;
  // With-batch mode
  batchId: number | null;
  // Standalone mode
  clientReference: string;
  farmerId: number | null;
  farmId: number | null;
  labInstructions: string;
  slaExpectedDate: Date | null;
}

/**
 * EntityAssignment schema for nullable object validation.
 */
const entityAssignmentSchema = yup.object({
  entityType: yup.string<'Farmer' | 'Agent' | 'Lab'>().required(),
  entityId: yup.number().required(),
  entityName: yup.string().required(),
  email: yup.string().nullable().optional(),
}).nullable().default(null);

/**
 * Creates a dynamic validation schema based on workflow flow type.
 *
 * @param flowType - The current workflow flow type ('NewRequest' or 'LabTriggered')
 * @returns A yup validation schema configured for the flow type
 */
export const createValidationSchema = (flowType: WorkflowFlowType): ObjectSchema<CreateBatchFormData> =>
  yup.object({
    // Batch ID required for ExistingBatch flow
    batchId: flowType === 'ExistingBatch'
      ? yup.number().nullable().required('Please select a batch')
      : yup.number().nullable().default(null),
    // Farmer required for NewRequest only; LabTriggered and ExistingBatch defer entity resolution
    farmerId: flowType === 'NewRequest'
      ? yup.number().nullable().required('Please select a farmer')
      : yup.number().nullable().default(null),
    // Common fields
    billingEntity: entityAssignmentSchema,
    supplier: entityAssignmentSchema,
    // Optional Brand (Partner) — no create, defaults to null when not selected
    brand: entityAssignmentSchema,
    // Optional Sales Agent (Agent only)
    salesAgent: entityAssignmentSchema,
    priority: yup.string<BatchPriority>().oneOf(['Urgent', 'High', 'Normal', 'Low']).required('Priority is required').default('Normal' as BatchPriority),
    // Standalone fields — farm required for NewRequest only; LabTriggered and ExistingBatch defer
    clientReference: yup.string().defined().default(''),
    farmId: flowType === 'NewRequest'
      ? yup.number().nullable().required('Please select a farm')
      : yup.number().nullable().default(null),
    labInstructions: yup.string().defined().default(''),
    slaExpectedDate: yup.date().nullable().default(null),
  }) as ObjectSchema<CreateBatchFormData>;
